import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import PostGameSeasonTable from '../components/postgame/PostGameSeasonTable';
import Spinner from '../components/Spinner';
import {
  fetchPostGameSeason,
  isPostGameSeasonError,
  updatePostGameSeasonTag,
} from '../services/postGameSeasonService';
import { DashboardOutletContextValue } from '../types/dashboard';
import { PostGameSeasonGame, PostGameSeasonResponse } from '../types/postGameSeason';

function PostGameSeasonPage() {
  const { username, selectedSport } = useOutletContext<DashboardOutletContextValue>();

  const [data, setData] = useState<PostGameSeasonResponse | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const loadData = useCallback(
    async (seasonOverride?: number) => {
      if (!username) {
        setError('Please sign in again to view post-season games.');
        setData(null);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchPostGameSeason({
          username,
          selectedSport,
          season: seasonOverride,
        });
        setData(response);
        if (seasonOverride === undefined) {
          setSelectedSeason(response.selectedSeason);
        }
      } catch (err) {
        console.error('Post-game season fetch failed:', err);
        setError('Unable to load post-season games.');
        setData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [username, selectedSport]
  );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSeasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    setSelectedSeason(value);
    void loadData(value);
  };

  const handleSave = async (row: PostGameSeasonGame, matchTypesText: string) => {
    if (!username) {
      setRowError('Please sign in again.');
      return;
    }

    setSavingId(row.id);
    setRowError(null);

    const result = await updatePostGameSeasonTag(
      { username, selectedSport },
      row.id,
      { matchTypes: matchTypesText }
    );

    setSavingId(null);

    if (isPostGameSeasonError(result)) {
      setRowError(result.message);
      return;
    }

    if (result.propagation?.status === 'queued') {
      setRowError(null);
      // Brief confirmation; list reload picks up lookup-driven tags immediately.
      const msg =
        result.propagation.taskId != null
          ? 'Saved. Stats are updating in the background.'
          : 'Saved. Background propagation was queued.';
      setSaveNotice(msg);
      window.setTimeout(() => setSaveNotice(null), 5000);
    }

    await loadData(selectedSeason ?? undefined);
  };

  const games = data?.games ?? [];
  const teamLabel = data?.teamName?.trim() || 'your team';
  const seasons = data?.availableSeasons ?? [];

  return (
    <section className="space-y-6">
      <header className="rounded-[22px] border border-slate-200 bg-white px-6 py-7 shadow-[0_24px_54px_-46px_rgba(15,23,42,0.65)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-sky-500">
              {selectedSport}
            </p>
            <h1 className="mt-3 text-[42px] font-semibold tracking-tight text-[#16233d]">
              Post-Season Games
            </h1>
            <p className="mt-3 max-w-2xl text-[18px] leading-8 text-[#65738d]">{teamLabel}</p>
          </div>
          <label className="flex min-w-[160px] flex-col gap-1 text-sm font-medium text-slate-700">
            Season
            <select
              value={selectedSeason ?? ''}
              onChange={handleSeasonChange}
              disabled={isLoading || seasons.length === 0}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-[#16233d] shadow-sm"
            >
              {seasons.length === 0 ? (
                <option value="">No seasons</option>
              ) : (
                seasons.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))
              )}
            </select>
          </label>
        </div>
      </header>

      {isLoading ? (
        <div className="flex min-h-[220px] items-center justify-center rounded-[22px] border border-slate-200 bg-white">
          <Spinner />
        </div>
      ) : error ? (
        <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-6 py-8 text-rose-700">
          {error}
        </div>
      ) : (
        <>
          {saveNotice ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {saveNotice}
            </div>
          ) : null}
          <PostGameSeasonTable
          games={games}
          sportLabel={selectedSport}
          season={selectedSeason}
          savingId={savingId}
          rowError={rowError}
          onSave={handleSave}
        />
        </>
      )}
    </section>
  );
}

export default PostGameSeasonPage;
